import * as React from "react";
import { Icon } from "../actions/Icon";
import { Button } from "../actions/Button";

/**
 * Inline notice across the top of a content area: permission prompts, "still uploading", one-time
 * tips. Never a modal. Its action is a secondary pill so it never competes with the page's CTA.
 */
export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Phosphor icon, tinted brand purple. Default "bell". Pass "" for none. */
  icon?: string;
  children: React.ReactNode;
  /** Short verb for the secondary action, e.g. "Turn on". */
  actionLabel?: string;
  onAction?: () => void;
  /** Shows a ghost dismiss button when provided. The caller hides the banner. */
  onDismiss?: () => void;
  /** Default "Dismiss". */
  dismissLabel?: string;
}

export function Banner({
  icon = "bell",
  children,
  actionLabel,
  onAction,
  onDismiss,
  dismissLabel = "Dismiss",
  className,
  ...rest
}: BannerProps): JSX.Element {
  return (
    <div role="status" className={["gk-banner", className].filter(Boolean).join(" ")} {...rest}>
      {icon && <Icon name={icon} size={18} className="gk-banner__icon" />}
      <div className="gk-banner__text">{children}</div>
      {(actionLabel || onDismiss) && (
        <div className="gk-banner__actions">
          {actionLabel && (
            <Button variant="secondary" size="sm" onClick={onAction}>
              {actionLabel}
            </Button>
          )}
          {onDismiss && (
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              {dismissLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
